<template>
  <!-- Negative-only feedback control: assistant answers only, once the answer
       is complete (not streaming / pending) and targets a persisted message.
       The reason picker is rendered in a floating-vue Dropdown so it teleports
       out of the chat's scroll/overflow container and auto-flips when there's
       no room above. -->
  <div v-if="canGiveFeedback" class="feedback-control">
    <Dropdown
      v-if="!isFlagged"
      :shown="showPopover"
      :triggers="['click']"
      :auto-hide="true"
      :distance="6"
      placement="top-end"
      popper-class="oliver-feedback-popper"
      @update:shown="onShownChange"
    >
      <button class="copy-btn feedback-toggle" type="button">
        <i class="fas fa-flag"></i>
        <span class="copy-btn-label">Report</span>
      </button>

      <template #popper>
        <div
          class="feedback-popover"
          role="dialog"
          aria-label="Report a problem"
          :style="themeVars"
        >
          <div class="feedback-popover-header">
            <span class="feedback-popover-title">What went wrong?</span>
            <button
              class="feedback-popover-close"
              type="button"
              aria-label="Close"
              @click="closePopover"
            >
              <i class="fas fa-times"></i>
            </button>
          </div>

          <ul class="feedback-reasons">
            <li v-for="option in reasons" :key="option.value">
              <div class="custom-control custom-checkbox">
                <input
                  v-model="selectedReasons"
                  type="checkbox"
                  :value="option.value"
                  :id="`feedback-reason-${option.value}`"
                  class="custom-control-input"
                />
                <label
                  class="custom-control-label"
                  :for="`feedback-reason-${option.value}`"
                  >{{ option.label }}</label
                >
              </div>
            </li>
          </ul>

          <textarea
            v-model="comment"
            class="feedback-comment"
            rows="2"
            placeholder="Add a comment (optional)"
          ></textarea>

          <div class="feedback-popover-actions">
            <button
              class="feedback-btn feedback-btn--ghost"
              type="button"
              @click="closePopover"
            >
              Cancel
            </button>
            <button
              class="feedback-btn feedback-btn--primary"
              type="button"
              :disabled="!selectedReasons.length || submitting"
              @click="handleSubmit"
            >
              {{ submitting ? "Submitting…" : "Submit" }}
            </button>
          </div>
        </div>
      </template>
    </Dropdown>

    <button
      v-else
      class="copy-btn feedback-toggle feedback-toggle--flagged"
      type="button"
      v-tooltip="thanks ? '' : 'Undo your report'"
      :disabled="thanks"
      @click="handleRemove"
    >
      <i :class="thanks ? 'fas fa-check' : 'fas fa-flag'"></i>
      <span class="copy-btn-label">{{
        thanks ? "Thanks!" : "Undo Report"
      }}</span>
    </button>
  </div>
</template>

<script>
import { Dropdown } from "floating-vue";
import { mapState, mapActions } from "vuex";
import { buildThemeVars } from "@/components/OliverBotV2/theme/themes.js";

/**
 * Matches a persisted (server) message id.
 * Server message ids are String(36) UUIDs; in-flight streamed messages carry
 * client-side ids like `assistant-<timestamp>`. Feedback can only target a
 * persisted message, so we gate on this shape.
 */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Negative-feedback reason categories.
 * NOTE: values MUST match the backend's FEEDBACK_REASONS set.
 */
export const FEEDBACK_REASONS = [
  { value: "inaccurate", label: "Inaccurate" },
  { value: "irrelevant", label: "Irrelevant" },
  { value: "incomplete", label: "Incomplete" },
  { value: "wrong_tone_or_format", label: "Wrong tone/format" },
  { value: "other", label: "Other" },
];

export default {
  name: "FeedbackPopover",
  components: { Dropdown },
  props: {
    message: {
      type: Object,
      required: true,
    },
    conversationId: {
      type: String,
      default: null,
    },
    isStreaming: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      reasons: FEEDBACK_REASONS,
      showPopover: false,
      submitting: false,
      thanks: false,
      selectedReasons: [],
      comment: "",
    };
  },
  computed: {
    ...mapState({
      oliverTheme: (state) => state.oliverChatBotV2.theme,
      oliverDarkMode: (state) => state.oliverChatBotV2.darkMode,
    }),
    // The popover is teleported to <body> by floating-vue, outside the element
    // that carries the --oliver-* theme vars. Re-apply them directly on the
    // popover so its themed colors (e.g. the Submit button accent) resolve.
    themeVars() {
      return buildThemeVars(this.oliverTheme, this.oliverDarkMode);
    },
    isAssistant() {
      return this.message.role !== "user" && this.message.system !== true;
    },
    feedbackTargetId() {
      // History messages carry the real id; a just-streamed answer carries the
      // persisted id in `server_id` (its `id` is a temp client id).
      return this.message.server_id || this.message.id;
    },
    hasServerId() {
      return UUID_RE.test(this.feedbackTargetId || "");
    },
    isFlagged() {
      return !!this.message.feedback;
    },
    canGiveFeedback() {
      return (
        this.isAssistant &&
        !this.isStreaming &&
        this.message.status !== "pending" &&
        this.hasServerId &&
        !!this.conversationId
      );
    },
  },
  methods: {
    ...mapActions("oliverChatBotV2", [
      "submitMessageFeedback",
      "removeMessageFeedback",
    ]),
    showFeedbackError(message) {
      this.$swal.fire({
        title: "Error",
        text: message,
        icon: "error",
      });
    },
    onShownChange(shown) {
      this.showPopover = shown;
      if (!shown) {
        this.selectedReasons = [];
        this.comment = "";
      }
    },
    closePopover() {
      this.showPopover = false;
    },
    async handleSubmit() {
      if (!this.selectedReasons.length) return;
      this.submitting = true;
      try {
        await this.submitMessageFeedback({
          chatId: this.conversationId,
          messageId: this.feedbackTargetId,
          reasons: this.selectedReasons,
          comment: this.comment.trim() || null,
        });
        this.showPopover = false;
        // Transient confirmation, mirroring the Copy button: the flag button
        // briefly shows a check + "Thanks!" before settling into "Undo Report".
        this.thanks = true;
        setTimeout(() => {
          this.thanks = false;
        }, 2000);
      } catch (error) {
        // Non-blocking: store already rolled back the optimistic flag.
        this.showFeedbackError("Couldn't save feedback. Please try again.");
        console.error("Failed to submit feedback:", error);
      } finally {
        this.submitting = false;
      }
    },
    async handleRemove() {
      try {
        await this.removeMessageFeedback({
          chatId: this.conversationId,
          messageId: this.feedbackTargetId,
        });
      } catch (error) {
        this.showFeedbackError("Couldn't remove feedback. Please try again.");
        console.error("Failed to remove feedback:", error);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.feedback-control {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

// Pill button base — shared with the Copy button in MessageBubble. Kept here so
// the feedback control is self-contained (it inherits the --oliver-* vars from
// the bubble it's rendered in).
.copy-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 20px;
  border: 1px solid transparent;
  background: transparent;
  cursor: pointer;
  transition: all 0.18s ease;
  font-size: 11px;
  flex-shrink: 0;
  color: var(--oliver-text);

  &:hover {
    opacity: 1;
    background: var(--oliver-accent-light, rgba(0, 0, 0, 0.08));
    border-color: var(--oliver-accent-border, rgba(0, 0, 0, 0.15));
    color: var(--oliver-accent);
  }

  &:active {
    transform: scale(0.95);
  }

  i.fa-check {
    color: var(--oliver-success);
  }
}

.copy-btn-label {
  font-size: 11px;
  font-weight: 500;
  // line-height 1 + overflow:hidden clipped descenders (e.g. the "p" in
  // "Report" / "Undo Report"); give the line a little vertical room.
  line-height: 1.35;
  letter-spacing: 0.1px;
  display: inline-block;
  text-align: left;
  white-space: nowrap;
}

// Reuses the .copy-btn pill styling, but more visible by default than Copy
// (Copy sits at 0.45) so the report affordance is easy to spot.
.feedback-toggle {
  opacity: 0.8;

  &--flagged {
    opacity: 1;
    color: var(--oliver-accent);

    i {
      color: var(--oliver-accent);
    }
  }

  // During the transient "Thanks!" confirmation the button is non-interactive
  // (no undo while it reads as a confirmation, not an action) but stays fully
  // visible — it's an acknowledgement, not a blocked control.
  &:disabled {
    cursor: default;
    opacity: 1;
  }
}

@media (max-width: 768px) {
  .copy-btn {
    font-size: 13px;
    padding: 4px 8px 4px 6px;
  }

  .copy-btn-label {
    font-size: 13px;
  }
}

// The reason picker itself, rendered inside the teleported popper.
.feedback-popover {
  // Rendered inside a floating-vue Dropdown popper (teleported to <body>). We
  // strip the popper's default white chrome (see the global style below) and
  // provide our OWN themed box here, so it follows the Oliver theme and dark
  // mode (the --oliver-* vars are applied on this element via :style).
  width: 240px;
  padding: 12px;
  text-align: left;
  background: var(--oliver-panel-bg, #fff);
  color: var(--oliver-text, #1a1a1a);
  border: 1px solid var(--oliver-border, rgba(0, 0, 0, 0.12));
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);

  &-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  &-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--oliver-text);
  }

  &-close {
    border: none;
    background: transparent;
    cursor: pointer;
    color: var(--oliver-text-muted);
    font-size: 12px;
    padding: 2px 4px;

    &:hover {
      color: var(--oliver-accent);
    }
  }

  &-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }

  .feedback-reasons {
    list-style: none;
    margin: 0 0 8px;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;

    // Use the app-wide custom checkbox (Bootstrap .custom-control) for
    // consistency with the rest of the CRM; only tune the size/colour to fit
    // the compact, themed popover.
    .custom-control-label {
      font-size: 13px;
      color: var(--oliver-text);
      cursor: pointer;

      &:hover {
        color: var(--oliver-accent);
      }
    }

    .custom-control-input {
      cursor: pointer;
    }
  }

  .feedback-comment {
    width: 100%;
    resize: vertical;
    border-radius: 8px;
    border: 1px solid var(--oliver-border, rgba(0, 0, 0, 0.15));
    padding: 6px 8px;
    font-size: 13px;
    font-family: inherit;
    color: var(--oliver-text);
    background: transparent;
    margin-bottom: 10px;

    &:focus {
      outline: none;
      border-color: var(--oliver-accent-border, var(--oliver-accent));
    }
  }

  .feedback-btn {
    border-radius: 8px;
    padding: 5px 12px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    border: 1px solid transparent;
    transition: all 0.15s ease;

    &--ghost {
      background: transparent;
      color: var(--oliver-text-muted);

      &:hover {
        color: var(--oliver-text);
      }
    }

    &--primary {
      // Fallback matches the default theme accent, for the rare case the
      // teleported popover hasn't received the --oliver-* vars yet.
      background: var(--oliver-accent, #c2713a);
      color: #fff;

      &:hover:not(:disabled) {
        filter: brightness(1.05);
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }
}
</style>

<!-- Global (non-scoped): the feedback popover is teleported to <body> by
     floating-vue, so scoped styles can't reach its wrapper. floating-vue's
     "dropdown" theme hard-codes a white box + arrow, which breaks dark mode.
     Strip that chrome here so our own themed .feedback-popover box shows. -->
<style lang="scss">
// Chain the theme class so this outranks floating-vue's own
// `.v-popper--theme-dropdown .v-popper__inner` rule — both classes are on the
// same popper element, and an equal-specificity selector alone lost the cascade
// (leaving the default white/6px-radius inner box peeking out at the corners).
.oliver-feedback-popper.v-popper--theme-dropdown {
  .v-popper__inner {
    background: transparent;
    border: none;
    box-shadow: none;
    padding: 0;
    // floating-vue's inner sets overflow-y:auto, which clips our themed box's
    // drop-shadow to a square rect (the corners flagged in review). The inner
    // is transparent/borderless, so let the shadow escape — the visible rounded
    // box is the .feedback-popover child, which carries its own radius.
    overflow: visible;
  }

  .v-popper__arrow-container {
    display: none;
  }
}
</style>
